/**
 * A barra de tempo no modo "Próximos dias" — toma o lugar da barra das 48 h
 * (decisão do julgamento: um relógio só por vez). Dias de Brasília como
 * botões (≥ 44 px, rolagem horizontal), ‹ › de 3 h/6 h, tocar, e "◂ Observado"
 * para voltar. Do 8º dia em diante, borda tracejada e "tendência".
 *
 * Cada dia tem, no pé, a RÉGUA dos passos dele (pedido do usuário, 17/10/2026:
 * há várias previsões por dia): uma casa por passo; no dia na tela, as casas
 * até o passo mostrado ficam cheias — tocando a previsão, o dia vai enchendo.
 * Só a cor e a espessura das casas mudam: nada muda de tamanho.
 *
 * Desktop: uma linha. Celular (pedido do usuário, 29/10/2026), como o player
 * da observação: na barra, ‹ e › grandes nas pontas e os dias no meio,
 * centralizados (rolam quando não cabem); no pé do mapa, "Voltar" à esquerda
 * (onde fica o relógio da observação), o relógio da previsão no meio e o play
 * à direita (onde fica a previsão das nuvens) — `PeDaPrevisao`.
 */
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, Pause, Play, Undo2 } from 'lucide-react';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { formatTimeBrt } from '../lib/time';
import { useNow, usePainelLateralAberto } from '../ui/hooks';
import { useDeitado } from '../ui/useMediaQuery';
import { useUiStore } from '../state/ui';
import { alternarTocar, andarPasso, definirModo, instantesDoModo } from './motor';
import { RelogioDaPrevisao } from './RelogioDaPrevisao';
import { usePrevisaoStore } from './store';
import { diasDaBarra, inicioDoDiaBrt, passoDoMeioDia, passoMaisProximo, reguaDoDia, rotuloDoInstante, type DiaDaBarra } from './tempo';

const DIAS_LONGOS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

function rotuloAcessivel(d: DiaDaBarra): string {
  const data = new Date(d.inicio - 3 * 3_600_000);
  const nome = d.hoje ? 'hoje' : `${DIAS_LONGOS[data.getUTCDay()]} ${data.getUTCDate()}`;
  return `Previsão de ${nome}${d.tendencia ? ' (tendência, menos confiável)' : ''}`;
}

/**
 * Um dia da barra. `cheias`: no dia na tela, quantas casas da régua estão
 * cheias (até o passo mostrado); -1 nos outros dias. Memorizado: tocando a
 * previsão, só o dia na tela redesenha.
 */
const BotaoDoDia = memo(function BotaoDoDia(props: {
  dia: DiaDaBarra;
  regua: (number | null)[];
  ativo: boolean;
  cheias: number;
  /** "passo 3 de 8, 18:00" (só no dia na tela). */
  passo: string | null;
  aoEscolher: (d: DiaDaBarra) => void;
  /** Celular: 48 px, a altura do ‹ e do › nas pontas. */
  alto?: boolean;
}) {
  const { dia, ativo } = props;
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (ativo) ref.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [ativo]);
  return (
    <button
      ref={ref}
      type="button"
      onClick={() => props.aoEscolher(dia)}
      aria-pressed={ativo}
      aria-label={`${rotuloAcessivel(dia)}${props.passo ? `, ${props.passo}` : ''}`}
      title={dia.tendencia ? 'Tendência: a partir do 8º dia a previsão é menos confiável' : undefined}
      data-dia-da-barra=""
      className={clsx(
        'flex w-[52px] shrink-0 flex-col items-center justify-center rounded-lg border px-1.5 pt-0.5 pb-1 text-xs leading-tight transition-colors',
        props.alto ? 'h-12' : 'min-h-11',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-violet-400',
        dia.tendencia ? 'border-dashed' : 'border-solid',
        ativo ? 'border-violet-400 bg-violet-500/25 text-white' : 'border-ink-600 bg-ink-800 text-ink-200 hover:bg-ink-700',
      )}
    >
      <span className="font-medium">{dia.hoje ? 'hoje' : dia.rotulo.split(' ')[0]}</span>
      <span className="tabular-nums">
        {dia.rotulo.split(' ')[1]}
        <span className="inline-block w-[1ch]">{dia.tendencia ? '~' : ''}</span>
      </span>
      {/* A régua (3 px): violeta = já mostrada no dia na tela; cinza = passo a ver; apagada = sem passo (já passou). */}
      <span className="mt-1 flex h-[3px] w-full gap-[2px]" aria-hidden data-regua="">
        {props.regua.map((k, i) => (
          <span
            key={i}
            className={clsx('h-[3px] flex-1 rounded-full', k === null ? 'bg-ink-700/50' : i < props.cheias ? 'bg-violet-300' : 'bg-ink-500')}
            data-casa={k === null ? 'vazia' : i < props.cheias ? 'cheia' : 'passo'}
          />
        ))}
      </span>
    </button>
  );
});

function BotaoRedondo(props: { label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode; className?: string }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      aria-label={props.label}
      title={props.label}
      className={clsx(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-xl border border-ink-600 bg-ink-800 text-white transition-colors hover:bg-ink-700 disabled:opacity-40',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-violet-400',
        props.className,
      )}
    >
      {props.children}
    </button>
  );
}

/** Celular: o ‹ e o › grandes nas pontas da barra, como os da observação (ui/TimeBar `PassoGrande`). */
function PassoGrande(props: { lado: 'voltar' | 'avancar'; disabled: boolean }) {
  const voltar = props.lado === 'voltar';
  const Icone = voltar ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={() => andarPasso(voltar ? -1 : 1)}
      disabled={props.disabled}
      aria-label={voltar ? 'Passo anterior (←)' : 'Próximo passo (→)'}
      className={clsx(
        'inline-flex size-12 shrink-0 items-center justify-center rounded-2xl border border-ink-600 bg-ink-800 text-white shadow-sm transition-colors active:bg-ink-600',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-violet-400 disabled:opacity-40',
      )}
    >
      <Icone className="size-7" aria-hidden />
    </button>
  );
}

/** Há dias escondidos à esquerda e à direita da faixa rolável? (para o esmaecer das pontas) */
function useBordasDaRolagem(ref: React.RefObject<HTMLElement | null>, deps: unknown[]): { esquerda: boolean; direita: boolean } {
  const [bordas, setBordas] = useState({ esquerda: false, direita: false });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => {
      const esquerda = el.scrollLeft > 1;
      const direita = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setBordas((b) => (b.esquerda === esquerda && b.direita === direita ? b : { esquerda, direita }));
    };
    medir();
    el.addEventListener('scroll', medir, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', medir);
      ro?.disconnect();
    };
  }, deps);
  return bordas;
}

function usePassos() {
  const indice = usePrevisaoStore((s) => s.indice);
  const rodadas = usePrevisaoStore((s) => s.rodadas);
  const tempo = usePrevisaoStore((s) => s.tempo);
  const instante = usePrevisaoStore((s) => s.instante);
  const instantes = useMemo(() => instantesDoModo({ indice, rodadas, tempo }), [indice, rodadas, tempo]);
  const k = instante !== null && instantes.length ? passoMaisProximo(instantes, instante) : -1;
  return { instantes, k };
}

/**
 * Celular: a linha do pé do mapa no "Próximos dias", na mesma grade do pé da observação
 * (ui/RelogioDoPe): "Voltar" à esquerda, o relógio da previsão no meio e o play à direita.
 */
export function PeDaPrevisao() {
  const tocando = usePrevisaoStore((s) => s.tocando);
  const exportando = usePrevisaoStore((s) => s.exportando);
  const folha = useUiStore((s) => s.sheetOpen);
  // A ficha do ponto aberta (previsao/EntradaDaPrevisao `FolhaDaPrevisao`): o pé fica acima dela (em pé)
  // ou à esquerda dela (deitado); só sai com a folha alta, que deixa pouco mapa à vista.
  const alta = usePrevisaoStore((s) => !!s.painel && s.encaixe === 'alta');
  const deitado = useDeitado();
  const { instantes } = usePassos();
  return (
    <div
      data-relogio-do-pe=""
      data-pe-da-previsao=""
      inert={exportando}
      className={clsx(
        'pointer-events-none absolute z-40 grid items-center gap-2',
        'left-[calc(8px+env(safe-area-inset-left))] deitado:left-[var(--trilho-fim)]',
        'grid-cols-[minmax(max-content,1fr)_minmax(0,auto)_minmax(44px,1fr)]',
        (folha || (alta && !deitado)) && 'hidden',
      )}
      style={{
        bottom: 'calc(8px + var(--folha-previsao-altura, 0px))',
        right: deitado ? 'calc(8px + var(--folha-previsao-largura, 0px))' : 'calc(8px + env(safe-area-inset-right))',
      }}
    >
      <div className="flex">
        <button
          type="button"
          onClick={() => definirModo(false)}
          aria-label="Voltar ao observado (imagens e medições das últimas 48 h)"
          title="Voltar às imagens e medições das últimas 48 h"
          className="glass pointer-events-auto inline-flex h-11 items-center gap-1.5 rounded-xl px-3 text-sm font-medium text-ink-100 shadow-lg hover:bg-ink-800 focus-visible:outline-2 focus-visible:outline-violet-400"
        >
          <Undo2 className="size-4" aria-hidden />
          Voltar
        </button>
      </div>
      <div className="glass pointer-events-auto flex h-11 min-w-0 items-center rounded-xl px-2.5 shadow-lg">
        <RelogioDaPrevisao rotuloCurto />
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={alternarTocar}
          disabled={instantes.length < 2}
          aria-label={tocando ? 'Pausar a previsão (espaço)' : 'Tocar a previsão (espaço)'}
          title={tocando ? 'Pausar a previsão' : 'Tocar a previsão'}
          data-tocar-previsao=""
          // Redondo e de 48 px, como o play da observação (e distinto do calendário quadrado da coluna).
          className="pointer-events-auto inline-flex size-12 items-center justify-center rounded-full bg-violet-600 text-white shadow-lg hover:bg-violet-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-300 disabled:opacity-40"
        >
          {tocando ? <Pause className="size-6 fill-current" aria-hidden /> : <Play className="size-6 translate-x-px fill-current" aria-hidden />}
        </button>
      </div>
    </div>
  );
}

export function BarraDaPrevisao(props: { mobile: boolean }) {
  const indice = usePrevisaoStore((s) => s.indice);
  const rodadas = usePrevisaoStore((s) => s.rodadas);
  const tempo = usePrevisaoStore((s) => s.tempo);
  const instante = usePrevisaoStore((s) => s.instante);
  const tocando = usePrevisaoStore((s) => s.tocando);
  // Exportando: a barra fica travada (o vídeo anda pelos passos sozinho).
  const exportando = usePrevisaoStore((s) => s.exportando);
  const now = useNow();
  const deitado = useDeitado();
  // Desktop: o relógio da previsão fica no chip do status (canto do mapa); com o painel lateral aberto
  // o chip some, e o relógio volta à barra — um só na tela.
  const painelAberto = usePainelLateralAberto();
  const instantes = useMemo(() => instantesDoModo({ indice, rodadas, tempo }), [indice, rodadas, tempo]);
  // Os dias só mudam com a rodada e a virada da hora.
  const hora = Math.floor(now / 3_600_000);
  const dias = useMemo(() => diasDaBarra(instantes, hora * 3_600_000), [instantes, hora]);
  const reguas = useMemo(() => dias.map((d) => reguaDoDia(instantes, d)), [dias, instantes]);
  const k = instante !== null && instantes.length ? passoMaisProximo(instantes, instante) : -1;
  const diaAtivo = instante !== null ? inicioDoDiaBrt(instante) : null;
  const primeiro = dias[0]?.passos[0] ?? 0;
  const { setTocando, setInstante } = usePrevisaoStore.getState();
  const escolherDia = useCallback(
    (d: DiaDaBarra) => {
      setTocando(false);
      // Hoje: o passo mais próximo de agora; os outros dias: o meio-dia.
      const alvo = d.hoje ? passoMaisProximo(instantes, Date.now()) : passoDoMeioDia(instantes, d);
      if (alvo >= 0) setInstante(instantes[alvo]);
    },
    [instantes, setTocando, setInstante],
  );

  // Celular em pé: só o ícone (44 px), para a linha caber os controles; o nome fica no rótulo.
  const voltarCompacto = props.mobile && !deitado;
  const voltar = (
    <button
      type="button"
      onClick={() => definirModo(false)}
      aria-label="Voltar ao observado (imagens e medições das últimas 48 h)"
      className={clsx(
        'inline-flex min-h-11 shrink-0 items-center justify-center gap-1 rounded-xl border border-ink-600 bg-ink-800 text-sm text-ink-100 hover:bg-ink-700 focus-visible:outline-2 focus-visible:outline-violet-400',
        voltarCompacto ? 'min-w-11 px-2' : 'px-2.5',
      )}
      title="Voltar às imagens e medições das últimas 48 h"
    >
      <Undo2 className="size-4" aria-hidden />
      {voltarCompacto ? <span className="text-xs">Voltar</span> : 'Observado'}
    </button>
  );
  const transporte = (
    <div className="flex shrink-0 items-center gap-1">
      <BotaoRedondo label="Passo anterior (←)" onClick={() => andarPasso(-1)} disabled={k <= primeiro}>
        <ChevronLeft className="size-6" aria-hidden />
      </BotaoRedondo>
      <BotaoRedondo
        label={tocando ? 'Pausar a previsão (espaço)' : 'Tocar a previsão (espaço)'}
        onClick={alternarTocar}
        disabled={instantes.length < 2}
        className="border-violet-500/70"
      >
        {tocando ? <Pause className="size-5" aria-hidden /> : <Play className="size-5" aria-hidden />}
      </BotaoRedondo>
      <BotaoRedondo label="Próximo passo (→)" onClick={() => andarPasso(1)} disabled={k < 0 || k >= instantes.length - 1}>
        <ChevronRight className="size-6" aria-hidden />
      </BotaoRedondo>
    </div>
  );
  const relogio = (
    <div className="flex shrink-0 flex-col items-end leading-tight">
      <span className="text-[11px] font-medium text-violet-300">Previsão</span>
      <span className="font-mono text-sm text-white tabular-nums">{instante !== null ? formatTimeBrt(instante) : '--:--'}</span>
    </div>
  );
  const diasDaLista = dias.map((d, i) => {
    const ativo = d.inicio === diaAtivo;
    const regua = reguas[i]!;
    // Casas cheias: até a do passo na tela (inclusive).
    const casa = ativo && k >= 0 ? regua.findIndex((x) => x !== null && x >= k) : -1;
    const cheias = ativo ? (casa < 0 ? regua.length : regua[casa] === k ? casa + 1 : casa) : -1;
    const comPasso = regua.filter((x) => x !== null);
    // O passo na tela fora da régua (um passo de hoje que já saiu da barra): sem "passo k de n".
    const pos = ativo && k >= 0 && instante !== null ? comPasso.indexOf(k) : -1;
    const passo = pos >= 0 ? `passo ${pos + 1} de ${comPasso.length}, ${rotuloDoInstante(instantes[k]!).slice(-5)}` : null;
    return <BotaoDoDia key={d.inicio} dia={d} regua={regua} ativo={ativo} cheias={cheias} passo={passo} aoEscolher={escolherDia} alto={props.mobile} />;
  });
  const faixa = useRef<HTMLDivElement>(null);
  const bordas = useBordasDaRolagem(faixa, [dias.length, props.mobile]);
  const listaDias = (
    // A ponta direita esmaece: há mais dias rolando (os de tendência ficam lá no celular em pé).
    <div
      className={clsx(
        'flex min-w-0 flex-1 gap-1 overflow-x-auto [scrollbar-width:thin]',
        '[mask-image:linear-gradient(to_right,black_calc(100%-44px),transparent)] pr-8',
        !props.mobile && 'py-0.5',
      )}
      role="group"
      aria-label="Dias da previsão (horário de Brasília)"
    >
      {dias.length === 0 ? (
        <span className="self-center px-2 text-xs text-ink-300">{indice ? 'Sem rodada de previsão disponível.' : 'Carregando a previsão…'}</span>
      ) : (
        diasDaLista
      )}
    </div>
  );

  if (props.mobile) {
    // Como a barra da observação: ‹ e › nas pontas, e os dias no meio — centralizados quando cabem,
    // rolando quando não (as pontas esmaecem do lado em que há mais dias).
    const mascara =
      bordas.esquerda && bordas.direita
        ? '[mask-image:linear-gradient(to_right,transparent,black_40px,black_calc(100%-40px),transparent)]'
        : bordas.direita
          ? '[mask-image:linear-gradient(to_right,black_calc(100%-40px),transparent)]'
          : bordas.esquerda
            ? '[mask-image:linear-gradient(to_right,transparent,black_40px)]'
            : '';
    return (
      <section
        data-timebar
        data-barra-previsao=""
        className="relative border-t border-violet-500/40 bg-ink-900 px-2 pt-1.5 pb-[calc(env(safe-area-inset-bottom)+6px)]"
        aria-label="Linha do tempo da previsão"
        inert={exportando}
      >
        <div className="flex items-center gap-2">
          <PassoGrande lado="voltar" disabled={k <= primeiro} />
          <div ref={faixa} className={clsx('min-w-0 flex-1 overflow-x-auto [scrollbar-width:none]', mascara)} role="group" aria-label="Dias da previsão (horário de Brasília)">
            {dias.length === 0 ? (
              <p className="py-3 text-center text-xs text-ink-300">{indice ? 'Sem rodada de previsão disponível.' : 'Carregando a previsão…'}</p>
            ) : (
              <div className="mx-auto flex w-max gap-1" data-dias-da-barra="">
                {diasDaLista}
              </div>
            )}
          </div>
          <PassoGrande lado="avancar" disabled={k < 0 || k >= instantes.length - 1} />
        </div>
      </section>
    );
  }
  return (
    <section data-timebar data-barra-previsao="" className="relative border-t border-violet-500/40 bg-ink-900 px-3 py-2" aria-label="Linha do tempo da previsão"
        inert={exportando}>
      <div className="flex items-center gap-3">
        {voltar}
        {transporte}
        {listaDias}
        {painelAberto && relogio}
      </div>
    </section>
  );
}
